import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraFormField, SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { deliveryClient, morgansClient } from '../../../lib/connect/clients';
import { useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { contractsClient } from './Contracts/api';
export function useCanRefreshCalcPlus() {
    const recordsEnabled = useRegistryRecordsEnabled();
    const areas = useTenantAreas();
    return recordsEnabled && areas.registries;
}
export function CalcPlusRefreshDialog({ onOpenChange }) {
    const allowed = useCanRefreshCalcPlus();
    return allowed ? /*#__PURE__*/ _jsx(RefreshDialog, {
        onOpenChange: onOpenChange
    }) : null;
}
function RefreshDialog({ onOpenChange }) {
    const tenantId = useTenantId();
    const { user } = useAuth();
    const qc = useQueryClient();
    const [contractNumbers, setContractNumbers] = useState([]);
    const [vendors, setVendors] = useState('');
    const aliases = useQuery({
        queryKey: [
            'calc-plus-contract-aliases',
            tenantId,
            user?.id
        ],
        retry: false,
        queryFn: async ()=>{
            const { vehicles } = await deliveryClient.listContractVehicles({
                companyId: tenantId
            });
            const profiles = await Promise.all(vehicles.filter((v)=>v.seedKey === 'gsa_mas').map((v)=>contractsClient.getVehicleProfile({
                    id: v.id
                })));
            // The profile read exposes alias text, without alias_type. Only MAS contract-number
            // shapes are offered; ordinary vehicle-name aliases are not refresh scopes.
            return [
                ...new Set(profiles.flatMap((p)=>p.profile?.aliases ?? []).filter((a)=>/^(GS[A-Z0-9]{8,}|47[A-Z0-9]{11,})$/.test(a.toUpperCase().replace(/[-\s]/g, ''))))
            ].sort();
        }
    });
    const refresh = useMutation({
        mutationFn: ()=>morgansClient.refreshCalcPlusBenchmarks({
                tenantId,
                contractNumbers,
                vendorNameFragments: [
                    ...new Set(vendors.split('\n').map((v)=>v.trim()).filter(Boolean))
                ]
            }),
        onSuccess: ()=>{
            void qc.invalidateQueries({
                queryKey: [
                    'registry',
                    'lcat-anchor'
                ]
            });
        }
    });
    const result = refresh.data;
    const budgetError = refresh.error && ConnectError.from(refresh.error).code === Code.ResourceExhausted;
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: refresh.isPending ? undefined : onOpenChange,
        size: "md",
        title: "Refresh CALC+ benchmarks",
        description: "CALC+ rates are a reference line. They never enter the rate book.",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            "data-testid": Testids.deliveryRegistries.calcPlusDialog,
            children: [
                /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "MAS contract numbers",
                    value: "",
                    placeholder: "Choose contract numbers",
                    loading: aliases.isPending,
                    disabled: refresh.isPending || aliases.isError,
                    options: (aliases.data ?? []).filter((a)=>!contractNumbers.includes(a)).map((a)=>({
                            value: a,
                            label: a
                        })),
                    onValueChange: (v)=>{
                        if (v) {
                            setContractNumbers((xs)=>[
                                    ...xs,
                                    v
                                ]);
                            refresh.reset();
                        }
                    },
                    emptyMessage: "No more MAS contract-number aliases on file."
                }),
                contractNumbers.map((number)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: number
                            }),
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                disabled: refresh.isPending,
                                onClick: ()=>{
                                    setContractNumbers((xs)=>xs.filter((x)=>x !== number));
                                    refresh.reset();
                                },
                                children: [
                                    "Remove ",
                                    number
                                ]
                            })
                        ]
                    }, number)),
                aliases.isError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    variant: "framed",
                    children: "Unable to load MAS contract numbers. Reopen this dialog to try again, or refresh by vendor name."
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Vendor names (optional)",
                    htmlFor: "calc-plus-vendors",
                    hint: "One vendor name fragment per line. Missing or ambiguous names are reported, never guessed.",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "calc-plus-vendors",
                        rows: 3,
                        disabled: refresh.isPending,
                        value: vendors,
                        onChange: (e)=>{
                            setVendors(e.target.value);
                            refresh.reset();
                        }
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "meta",
                    tone: "muted",
                    children: "Choose at least one contract number or enter a vendor name."
                }),
                refresh.error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    children: budgetError ? 'CALC+ request budget exhausted. Try again after the daily budget resets.' : `Unable to refresh CALC+ benchmarks: ${ConnectError.from(refresh.error).rawMessage}`
                }),
                result && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: result.budgetExhausted ? 'warning' : 'success',
                    variant: "framed",
                    children: [
                        /*#__PURE__*/ _jsxs("p", {
                            children: [
                                result.rows,
                                " benchmark rows · ",
                                result.vendorsMissing.length,
                                " vendors not found ·",
                                ' ',
                                result.vendorsAmbiguous.length,
                                " ambiguous"
                            ]
                        }),
                        result.vendorsMissing.length > 0 && /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Not found: ",
                                result.vendorsMissing.join(', ')
                            ]
                        }),
                        result.vendorsAmbiguous.length > 0 && /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Ambiguous: ",
                                result.vendorsAmbiguous.join(', ')
                            ]
                        }),
                        result.retrievedAt && /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Retrieved: ",
                                result.retrievedAt
                            ]
                        }),
                        result.budgetExhausted && /*#__PURE__*/ _jsx("p", {
                            children: "CALC+ request budget exhausted. The refresh is incomplete; try again after the daily budget resets."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            disabled: refresh.isPending,
                            onClick: ()=>onOpenChange(false),
                            children: "Close"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.deliveryRegistries.calcPlusSubmit,
                            isLoading: refresh.isPending,
                            disabled: refresh.isPending || !contractNumbers.length && !vendors.trim(),
                            onClick: ()=>refresh.mutate(),
                            children: "Refresh benchmarks"
                        })
                    ]
                })
            ]
        })
    });
}
